import { supabase } from '@/lib/supabase';
import type {
  ApiKey,
  ApiKeyWithOwner,
  CreatedKey,
  KeyMetrics,
  KeyRequestLogEntry,
} from './types';

const SELECT_COLUMNS =
  'id, name, prefix, scopes, ip_allowlist, last_used_at, expires_at, revoked_at, created_at';

export async function listMyKeys(): Promise<ApiKey[]> {
  const { data, error } = await supabase
    .from('api_key')
    .select(SELECT_COLUMNS)
    .is('revoked_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ApiKey[];
}

export async function listAllKeys(): Promise<ApiKey[]> {
  const { data, error } = await supabase
    .from('api_key')
    .select(SELECT_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ApiKey[];
}

export async function listAllKeysWithOwner(): Promise<ApiKeyWithOwner[]> {
  // RLS gates which rows the caller sees: 'own' scope returns only their own,
  // 'company'/'platform' return tenant-wide. The pessoa join surfaces the
  // owner column for the admin view; we disambiguate the FK because api_key
  // has multiple FKs to pessoa (created_by, updated_by, revoked_by).
  const { data, error } = await supabase
    .from('api_key')
    .select(`${SELECT_COLUMNS}, pessoa_id, pessoa:pessoa!api_key_pessoa_id_fkey(nome)`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  type Row = ApiKey & {
    pessoa_id: string;
    pessoa: { nome: string | null } | { nome: string | null }[] | null;
  };
  return (data ?? []).map((raw) => {
    const row = raw as unknown as Row;
    const pessoa = Array.isArray(row.pessoa) ? row.pessoa[0] : row.pessoa;
    const { pessoa: _omit, ...rest } = row;
    return { ...rest, owner_nome: pessoa?.nome ?? null };
  }) as ApiKeyWithOwner[];
}

export async function createApiKey(input: {
  name: string;
  scopes: string[];
  ipAllowlist?: string[] | null;
  expiresAt?: string | null;
}): Promise<CreatedKey> {
  const { data, error } = await supabase.rpc('rpc_api_key_create', {
    p_name: input.name,
    p_scopes: input.scopes,
    p_ip_allowlist: input.ipAllowlist ?? undefined,
    p_expires_at: input.expiresAt ?? undefined,
  });
  if (error) throw error;
  return data as unknown as CreatedKey;
}

export async function revokeApiKey(id: string): Promise<void> {
  const { error } = await supabase
    .from('api_key')
    .update({ revoked_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function getKeyMetrics(keyId: string, hours = 24): Promise<KeyMetrics> {
  const { data, error } = await supabase.rpc('rpc_api_key_metrics', {
    p_key_id: keyId,
    p_hours: hours,
  });
  if (error) throw error;
  return data as unknown as KeyMetrics;
}

/**
 * Fire-and-forget welcome email after creating an API key.
 *
 * The edge function does the authoritative "is this the first key" check
 * (counts including revoked, service-role) and silently returns `skipped:true`
 * when not first. Caller-target binding inside the function ensures we can only
 * trigger an email for the JWT's own pessoa.
 *
 * Errors are swallowed by the caller — email is a UX-nice-to-have and must
 * never block the post-creation modal.
 */
export async function sendApiWelcomeEmail(input: {
  userEmail: string;
  userName?: string;
  keyName?: string;
}): Promise<void> {
  const { error } = await supabase.functions.invoke('send-api-welcome-email', {
    body: input,
  });
  if (error) throw error;
}

export async function listRecentRequests(keyId: string, limit = 20): Promise<KeyRequestLogEntry[]> {
  const { data, error } = await supabase
    .from('api_request_log')
    .select('id, request_id, method, route, status, latency_ms, ip, created_at')
    .eq('api_key_id', keyId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as KeyRequestLogEntry[];
}
